import { CsvImportPicker } from '@/components/CsvImportPicker';
import { LeadScrapeTargetingPanel } from '@/components/LeadScrapeTargetingPanel';
import { Badge } from '@/components/ui/Badge';
import { Card, CardContent, CardHeader } from '@/components/ui/Card';
import { Checkbox } from '@/components/ui/Checkbox';
import { Switch } from '@/components/ui/Input';
import { ENRICHMENT_PROVIDERS } from '@/lib/enrichment-providers';
import { useWizardStore } from '../lib/wizard-store';

function ProviderTile({
  checked,
  onToggle,
  provider,
}: {
  checked: boolean;
  onToggle: () => void;
  provider: (typeof ENRICHMENT_PROVIDERS)[number];
}) {
  const Icon = provider.icon;

  return (
    <div
      className={`rounded border p-4 text-left transition-colors ${
        checked
          ? 'border-brand-primary bg-brand-surface'
          : 'border-brand-border bg-white hover:border-brand-primary/40'
      }`}
      data-testid={`provider-tile-${provider.id}`}
    >
      <div className="mb-3 flex items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <div
            className={`flex h-9 w-9 items-center justify-center rounded-full ${
              checked ? 'bg-brand-primary text-white' : 'bg-gray-100 text-gray-600'
            }`}
          >
            <Icon size={16} />
          </div>
          <div>
            <div className="text-sm font-semibold text-brand-dark">{provider.label}</div>
            <div className="text-xs text-gray-500">{provider.statusLabel}</div>
          </div>
        </div>
        <Checkbox
          id={`provider-${provider.id}`}
          checked={checked}
          onChange={() => onToggle()}
          aria-label={provider.label}
          data-testid={`provider-${provider.id}`}
        />
      </div>
      <p className="text-sm text-gray-600">{provider.description}</p>
    </div>
  );
}

export function W7_Enrichisseurs() {
  const {
    enrichment_providers,
    enrichment_force_complete,
    leadscrape_targeting,
    csv_import_draft,
    toggleEnrichmentProvider,
    setEnrichmentForceComplete,
    setLeadScrapeTargeting,
    setCsvImportDraft,
    sender_company_name,
    sender_description,
    sender_website,
    offer_title,
    offer_description,
    offer_url,
    target_country,
    target_region,
    language,
  } = useWizardStore();

  const csvEnabled = enrichment_providers.includes('csv');
  const leadscrapeEnabled = enrichment_providers.includes('leadscrape');
  const emailSleuthEnabled = enrichment_providers.includes('emailsleuth');
  const hubspotEnabled = enrichment_providers.includes('hubspot');
  const bigqueryEnabled = enrichment_providers.includes('bigquery');

  return (
    <Card>
      <CardHeader
        title="Enrichissement & sources"
        subtitle="Choisis une ou plusieurs sources pour alimenter la campagne maintenant, puis continuer plus tard depuis le detail campagne."
      />
      <CardContent className="space-y-5" data-testid="w7-enrichisseurs-section">
        <div className="grid gap-3 xl:grid-cols-2">
          {ENRICHMENT_PROVIDERS.map((provider) => (
            <ProviderTile
              key={provider.id}
              provider={provider}
              checked={enrichment_providers.includes(provider.id)}
              onToggle={() => toggleEnrichmentProvider(provider.id)}
            />
          ))}
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {enrichment_providers.length === 0 ? (
            <Badge tone="yellow">Aucune source active pour le moment</Badge>
          ) : (
            enrichment_providers.map((providerId) => (
              <Badge key={providerId} tone="brand">
                {ENRICHMENT_PROVIDERS.find((provider) => provider.id === providerId)?.label ??
                  providerId}
              </Badge>
            ))
          )}
        </div>

        <div className="flex items-center justify-between py-1" data-testid="force-complete-row">
          <span className="text-sm text-gray-700">
            Force complete{' '}
            <span className="text-xs text-gray-400">(re-enrich meme si en cache 6 mois)</span>
          </span>
          <Switch checked={enrichment_force_complete} onChange={setEnrichmentForceComplete} />
        </div>

        {csvEnabled && (
          <CsvImportPicker
            value={csv_import_draft}
            onChange={setCsvImportDraft}
            title="Import CSV initial"
            subtitle="Charge ici le premier lot de leads. Si tu veux continuer plus tard, la campagne gardera aussi cette capacite depuis son detail."
            dataTestId="w7-csv-import"
          />
        )}

        {leadscrapeEnabled && (
          <>
            <hr className="border-brand-border" />
            <LeadScrapeTargetingPanel
              value={leadscrape_targeting}
              onChange={setLeadScrapeTargeting}
              campaignContext={{
                sender_company: sender_company_name,
                sender_description,
                sender_website,
                offer_title,
                offer_description,
                offer_url,
                target: target_country,
                target_region,
                language,
              }}
            />
          </>
        )}

        {(emailSleuthEnabled || hubspotEnabled || bigqueryEnabled) && (
          <div
            className="rounded border border-brand-border bg-gray-50 px-4 py-3 text-sm text-gray-600"
            data-testid="campaign-source-note"
          >
            {emailSleuthEnabled && (
              <p>
                EmailSleuth est memorise comme enrichisseur actif pour les lots ou imports qui
                arrivent deja avec au minimum un nom complet et un domaine de societe.
              </p>
            )}
            {hubspotEnabled && (
              <p className={emailSleuthEnabled ? 'mt-2' : ''}>
                HubSpot est memorise comme source active de campagne pour les continuations et
                imports rattaches a ce workflow.
              </p>
            )}
            {bigqueryEnabled && (
              <p className={emailSleuthEnabled || hubspotEnabled ? 'mt-2' : ''}>
                BigQuery est memorise comme source active de campagne pour les continuations et
                imports rattaches a ce workflow.
              </p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
